import React, { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Container, Typography, Box, CircularProgress, Alert, Tooltip, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton } from "@mui/material";
import { API_ENDPOINTS } from "../config/api.config";
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

export default function Customers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 5,
  });

  // Stato per il dialog di create/update
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    description: '',
  });
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Fetch customers
  const fetchCustomers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(API_ENDPOINTS.CUSTOMERS);
      if (!response.ok) throw new Error("Error fetching customers");
      const data = await response.json();
      const customers = data.customers || [];
      console.log('Customers loaded:', customers);
      setRows(customers.map((row, idx) => ({ id: row.id || idx, ...row })));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  // Colonne della tabella
  const columns = [
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton onClick={() => handleEditDialog(params.row)} size="small">
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton onClick={() => handleDelete(params.row.id)} size="small">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    },
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'name', headerName: 'Name', width: 200 },
    { field: 'description', headerName: 'Description', width: 300 },
    { 
      field: 'created_at', 
      headerName: 'Created At', 
      width: 160,
      valueFormatter: (params) => {
        if (!params.value) return '';
        const date = new Date(params.value);
        return date.toLocaleString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
      }
    },
    { 
      field: 'updated_at', 
      headerName: 'Updated At', 
      width: 160,
      valueFormatter: (params) => {
        if (!params.value) return '';
        const date = new Date(params.value);
        return date.toLocaleString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
      }
    },
  ];

  // Gestione dialog
  const handleOpenDialog = () => {
    setFormData({
      id: '',
      name: '',
      description: '',
    });
    setEditingRow(null);
    setFormError(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormError(null);
  };

  const handleEditDialog = (row) => {
    setFormData({
      id: row.id || '',
      name: row.name || '',
      description: row.description || '',
    });
    setEditingRow(row);
    setFormError(null);
    setOpenDialog(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Salva customer (create/update)
  const handleSaveCustomer = async () => {
    setSaving(true);
    setFormError(null);
    
    if (!formData.name.trim()) {
      setFormError('Name is required.');
      setSaving(false);
      return;
    }

    try {
      let payload, url, method;
      
      if (editingRow) {
        // Update - usa PUT con query parameter
        payload = {
          name: formData.name.trim(),
          description: formData.description.trim() || null,
        };
        url = `${API_ENDPOINTS.CUSTOMERS}?id=${editingRow.id}`;
        method = 'PUT';
      } else {
        // Create - non includere l'ID
        payload = {
          name: formData.name.trim(),
          description: formData.description.trim() || null,
        };
        url = API_ENDPOINTS.CUSTOMERS;
        method = 'POST';
      }

      console.log(`[CUSTOMERS] ${method} request to:`, url);
      console.log('[CUSTOMERS] Payload:', payload);

      const response = await fetch(url, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      console.log('[CUSTOMERS] Response status:', response.status);
      
      if (!response.ok) {
        const err = await response.text();
        console.log('[CUSTOMERS] Error response:', err);
        throw new Error(`${response.status}: ${err || 'Request failed'}`);
      }

      setOpenDialog(false);
      setEditingRow(null);
      // Ricarica customers
      await fetchCustomers();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Elimina customer
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_ENDPOINTS.CUSTOMERS}?id=${id}`, { 
        method: 'DELETE' 
      });
      if (!response.ok) throw new Error('Error deleting customer');
      await fetchCustomers();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box my={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4" gutterBottom sx={{ fontSize: '1.5rem' }}>
            Customers Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            color="primary"
            onClick={handleOpenDialog}
          >
            Add Customer
          </Button>
        </Box>
        
        {loading ? (
          <CircularProgress />
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Box sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={rows}
              columns={columns}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[5, 10, 25, 50, 100]}
              pagination
              hideFooter={false}
              sx={{ bgcolor: 'white' }}
              disableSelectionOnClick
            />
          </Box>
        )}
      </Box>

      {/* Dialog per create/update customer */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingRow ? 'Edit Customer' : 'New Customer'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              name="name"
              label="Name"
              value={formData.name}
              onChange={handleFormChange}
              required
              fullWidth
              error={!formData.name.trim() && formError}
              helperText={!formData.name.trim() && formError ? 'Name is required' : ''}
            />
            <TextField
              name="description"
              label="Description"
              value={formData.description}
              onChange={handleFormChange}
              multiline
              rows={3}
              fullWidth
            />
            {formError && <Alert severity="error">{formError}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveCustomer} 
            variant="contained" 
            color="primary" 
            disabled={saving}
          >
            {saving ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 